import React, { useState, useEffect, useMemo } from 'react';
import './TempTracker.css';
import { Line } from 'react-chartjs-2';
import { getDatabase, ref, onValue } from 'firebase/database';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import Navbar from './components/Navbar';
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LineController,
  TimeScale,
  LinearScale,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import 'chartjs-adapter-date-fns';

// Register the components
ChartJS.register(
  LineElement,
  PointElement,
  LineController,
  TimeScale,
  LinearScale,
  Title,
  Tooltip,
  Legend
);

const TempTracker = () => {
  const [temperatures, setTemperatures] = useState([]);
  const navigate = useNavigate();

  const db = getDatabase();

  useEffect(() => {
    const temperaturesRef = ref(db, 'temperatures/');
    const unsubscribe = onValue(temperaturesRef, (snapshot) => {
      const data = snapshot.val();
      const newTemperatures = data ? Object.keys(data).map(key => ({
        time: moment(data[key].time, 'HH:mm:ss').toDate(),
        temperature: data[key].temperature
      })) : [];

      setTemperatures(newTemperatures);
    });

    return () => unsubscribe();
  }, [db]);

  const latestTemp = useMemo(() => {
    if (temperatures.length === 0) return null;
    return temperatures[temperatures.length - 1];
  }, [temperatures]);

  const minTemp = useMemo(() => {
    if (temperatures.length === 0) return null;
    return Math.min(...temperatures.map(t => t.temperature));
  }, [temperatures]);

  const maxTemp = useMemo(() => {
    if (temperatures.length === 0) return null;
    return Math.max(...temperatures.map(t => t.temperature));
  }, [temperatures]);

  const chartData = {
    labels: temperatures.map(temp => temp.time),
    datasets: [
      {
        label: 'Temperature',
        data: temperatures.map(temp => temp.temperature),
        fill: true,
        borderColor: '#2563eb',
        backgroundColor: 'rgba(37, 99, 235, 0.1)',
        pointBackgroundColor: '#1d4ed8',
        pointBorderColor: '#e2e8f0',
        pointRadius: 4,
        tension: 0.3
      }
    ]
  };

  const options = {
    scales: {
      x: {
        type: 'time',
        time: {
          unit: 'minute',
          tooltipFormat: 'HH:mm:ss'
        },
        title: {
          display: true,
          text: 'Time',
          color: '#0f172a'
        },
        ticks: { color: '#475569' },
        grid: { color: 'rgba(148, 163, 184, 0.2)' }
      },
      y: {
        title: {
          display: true,
          text: 'Temperature (°C)',
          color: '#0f172a'
        },
        ticks: { color: '#475569' },
        grid: { color: 'rgba(148, 163, 184, 0.2)' }
      }
    },
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        callbacks: {
          label: (ctx) => `${ctx.formattedValue} °C`
        }
      }
    },
    maintainAspectRatio: false
  };

  return (
    <div className="temp-tracker-page">
      <Navbar title="Temperature Log" />

      <div className="temp-tracker-container">
        <header className="temp-header">
          <div>
            <p className="eyebrow">Food Safety</p>
            <h1>Temperature Tracker</h1>
            <p className="subtitle">Monitor and review temperature logs in real-time.</p>
          </div>
          <button className="primary-btn" onClick={() => navigate('/home')}>Back to Dashboard</button>
        </header>

        <div className="metrics-grid">
          <div className="metric-card">
            <span className="metric-label">Latest Reading</span>
            <strong className="metric-value">
              {latestTemp ? `${latestTemp.temperature} °C` : '--'}
            </strong>
            <span className="metric-sub">
              {latestTemp ? `at ${moment(latestTemp.time).format('HH:mm:ss')}` : 'No data yet'}
            </span>
          </div>
          <div className="metric-card">
            <span className="metric-label">Min Today</span>
            <strong className="metric-value">{minTemp !== null ? `${minTemp} °C` : '--'}</strong>
          </div>
          <div className="metric-card">
            <span className="metric-label">Max Today</span>
            <strong className="metric-value">{maxTemp !== null ? `${maxTemp} °C` : '--'}</strong>
          </div>
        </div>

        <div className="card chart-card">
          <div className="card-header">
            <div>
              <h3>Temperature over Time</h3>
              <p>Live updates from your probes.</p>
            </div>
          </div>
          <div className="chart-container">
            {temperatures.length === 0 ? (
              <div className="empty-state">
                <p>No temperature data yet.</p>
              </div>
            ) : (
              <Line data={chartData} options={options} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TempTracker;
